import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Home, Monitor, Star, User, Calendar, ShieldCheck } from "lucide-react";
import { isAdminMerchantMode, exitAdminMerchantMode } from "../utils/adminMerchantMode";

const TABS = [
  {
    label: "Home",
    icon: Home,
    route: "/home",
  },
  {
    label: "TV",
    icon: Monitor,
    route: "/modes",
  },
  {
    label: "Schedule",
    icon: Calendar,
    route: "/schedule-hub",
  },
  {
    label: "AI Menu",
    icon: Star,
    route: "/ai-menu",
  },
  {
    label: "Profile",
    icon: User,
    route: "/user-settings",
  },
];

const isTabActive = (route, pathname) => {
  if (route === "/home") {
    return pathname === "/home";
  }
  if (route === "/modes") {
    return (
      pathname === "/modes" ||
      pathname.startsWith("/promo-ads/")
    );
  }
  if (route === "/schedule-hub") {
    return (
      pathname === "/schedule-hub" ||
      pathname === "/schedule" ||
      pathname.startsWith("/create-schedule")
    );
  }
  if (route === "/ai-menu") {
    return (
      pathname === "/ai-menu" ||
      pathname === "/extract-data" ||
      pathname === "/menu-preview"
    );
  }
  return (
    pathname === route ||
    pathname.startsWith(`${route}/`)
  );
};

// Tabs hidden from an admin browsing in Merchant Mode — admins get there
// via Login as Merchant, not the normal Home/Profile flow.
const ADMIN_MERCHANT_MODE_HIDDEN_TABS = ["Home", "Profile"];

/**
 * Shared mobile app footer (bottom tab navigation).
 */
const AppFooter = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Only true for an admin impersonating a merchant (admin_token +
  // customer_id both set) — never true for a normal merchant/user session,
  // so their nav is completely unaffected.
  const adminMerchantMode = isAdminMerchantMode();
  const tabs = adminMerchantMode
    ? TABS.filter((tab) => !ADMIN_MERCHANT_MODE_HIDDEN_TABS.includes(tab.label))
    : TABS;

  const handleBackToAdmin = () => {
    exitAdminMerchantMode();
    navigate("/admin/dashboard");
  };

  return (
    <footer
      className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-t border-gray-100 flex justify-around items-center py-2 safe-area-pb"
      role="navigation"
      aria-label="Main navigation"
    >
      {tabs.map(({ label, icon: Icon, route }) => {
        const isActive = isTabActive(route, pathname);
        return (
          <button
            key={label}
            type="button"
            onClick={() => navigate(route)}
            className={`flex flex-col items-center gap-0.5 min-w-[3rem] py-1 transition-colors duration-200 ${
              isActive ? "text-blue-600" : "text-gray-400 hover:text-gray-600"
            }`}
          >
            <Icon size={20} />
            <span className="text-xs font-medium">{label}</span>
            {isActive && (
              <div className="h-0.5 w-5 bg-blue-600 rounded-full mt-0.5" />
            )}
          </button>
        );
      })}

      {adminMerchantMode && (
        <button
          type="button"
          onClick={handleBackToAdmin}
          className="flex flex-col items-center gap-0.5 min-w-[3rem] py-1 text-amber-600 hover:text-amber-700 transition-colors duration-200"
        >
          <ShieldCheck size={20} />
          <span className="text-xs font-medium">Back to Admin</span>
        </button>
      )}
    </footer>
  );
};

export default AppFooter;
